/**
 * Frontend API client for the Category Pulse tab (Chase Brief #001).
 * Maps 1:1 to /v1/observe/dbview/chase/* in backend/product/src/api/server.ts.
 * Field definitions are locked in backend/product/docs/CHASE_DATA_DICTIONARY.md.
 */

import { request } from './client';

export type PulseSort = 'velocity' | 'spread' | 'listings' | 'modelKey';

export interface PulseRow {
  modelKey: string;
  category: string | null;
  condition: 'A' | 'B' | 'C' | null;
  // SOLD (eBay sold) — windowed
  sold_median_30d: number | null;
  sold_low_7d: number | null;
  sold_avg_30d: number | null;
  sold_high_30d: number | null;
  sold_count_7d: number;
  sold_count_30d: number;
  last_sold_at: string | null;
  // ASKING (live listings) — current snapshot
  ask_low: number | null;
  ask_median: number | null;
  ask_count: number;
  // derived
  spread_pct: number | null;
}

export interface PulseResp {
  ok: true;
  sinceMs: number;
  category: string | null;
  generatedAt: string;
  rows: PulseRow[];
}

function qs(params: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export interface PulseOpts {
  category?: string;
  modelKey?: string;
  sinceMs?: number;
  limit?: number;
  sort?: PulseSort;
}

export function getCategoryPulse(opts: PulseOpts = {}) {
  return request<PulseResp>(`/v1/observe/dbview/chase/pulse${qs(opts as Record<string, unknown>)}`);
}

export function pulseExportPath(opts: PulseOpts = {}): string {
  return `/v1/observe/dbview/chase/pulse/export${qs(opts as Record<string, unknown>)}`;
}

// ── ModelKey detail (drill-down: anchor breakdown / raw sold / filter flags) ──

export interface AnchorSourceBreakdown {
  source: string;
  price: number;
  sampleCount: number | null;
  confidence: number | null;
}
export interface SoldRaw {
  ts: string | null;
  price: number;
  condition: string | null;
  title: string | null;
  source: string | null;
  url: string | null;
}
export interface FilterFlagSummary {
  status: string;
  count: number;
  sampleReasons: string[];
}
export interface ModelKeyDetailResp {
  ok: true;
  modelKey: string;
  category: string | null;
  anchor: {
    final: number | null;
    winner: string | null;
    confidence: number | null;
    sources: AnchorSourceBreakdown[];
    fromDealId: string | null;
    computedAt: string | null;
  } | null;
  soldCount: number;
  soldRaw: SoldRaw[];
  filterFlags: FilterFlagSummary[];
}

export function getModelKeyDetail(modelKey: string) {
  return request<ModelKeyDetailResp>(`/v1/observe/dbview/chase/modelkey${qs({ modelKey })}`);
}

// ── ModelKey pipeline trace (2.7) ──

export interface TraceStage { stage: string; count: number; note?: string }
export interface ModelKeyTraceResp {
  ok: true;
  modelKey: string;
  category: string | null;
  sinceMs: number;
  stages: TraceStage[];
  dropReasons: Array<{ reason: string; count: number }>;
}

export function getModelKeyTrace(modelKey: string) {
  return request<ModelKeyTraceResp>(`/v1/observe/dbview/chase/trace${qs({ modelKey })}`);
}
